import * as THREE from 'three';
import { ObstacleType } from '@lucky-vr/common';
import { darkMetalMaterial, emissiveMaterial, paintedMetalMaterial } from '../core/MaterialLibrary.js';

interface ObstacleVisual {
  group: THREE.Group;
  type: ObstacleType;
  damageTier: number;
}

/**
 * Static road hazards. Unlike enemies they never move, so each mesh is
 * posed once when it appears in server state.
 */
export class ObstacleManager {
  private visuals = new Map<string, ObstacleVisual>();

  constructor(private parent: THREE.Group) {}

  sync(
    ids: Set<string>,
    describe: (id: string) => {
      type: ObstacleType;
      position: THREE.Vector3;
      quaternion: THREE.Quaternion;
      health: number;
      maxHealth: number;
    }
  ): void {
    for (const id of ids) {
      const state = describe(id);
      let visual = this.visuals.get(id);
      if (!visual) {
        const group = this.build(state.type);
        group.position.copy(state.position);
        group.quaternion.copy(state.quaternion);
        this.parent.add(group);
        visual = { group, type: state.type, damageTier: 0 };
        this.visuals.set(id, visual);
      }
      this.paintDamage(visual, state.health / Math.max(1, state.maxHealth));
      if (visual.type === ObstacleType.RepairKit) {
        const halo = visual.group.userData.repairHalo as THREE.Object3D | undefined;
        if (halo) {
          const pulse = 1 + Math.sin(performance.now() * 0.006) * 0.08;
          halo.scale.setScalar(pulse);
          halo.rotation.z += 0.045;
        }
      }
    }
    for (const id of [...this.visuals.keys()]) {
      if (!ids.has(id)) this.remove(id);
    }
  }

  remove(id: string): void {
    const visual = this.visuals.get(id);
    if (!visual) return;
    this.parent.remove(visual.group);
    visual.group.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry.dispose();
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
        mats.forEach((m) => {
          if ('map' in m && m.map instanceof THREE.Texture) m.map.dispose();
          m.dispose();
        });
      }
    });
    this.visuals.delete(id);
  }

  clear(): void {
    for (const id of [...this.visuals.keys()]) this.remove(id);
  }

  private build(type: ObstacleType): THREE.Group {
    const group = new THREE.Group();
    const solid = (color: number, roughness = 0.62) => new THREE.MeshStandardMaterial({ color, roughness, metalness: 0.08 });

    switch (type) {
      case ObstacleType.Barrel: {
        const drum = new THREE.Mesh(new THREE.CylinderGeometry(0.45, 0.45, 1.1, 10), paintedMetalMaterial(0xa73416, 0.4));
        drum.position.y = 0.55;
        drum.castShadow = true;
        group.add(drum);
        const band = new THREE.Mesh(new THREE.CylinderGeometry(0.46, 0.46, 0.18, 10), solid(0xc8cdd0, 0.38));
        band.position.y = 0.55;
        group.add(band);
        break;
      }

      case ObstacleType.Barrier: {
        const plank = new THREE.Mesh(new THREE.BoxGeometry(3.0, 0.45, 0.22), paintedMetalMaterial(0xc54d13, 0.44));
        plank.position.y = 0.75;
        plank.castShadow = true;
        group.add(plank);
        for (const x of [-1.0, 0, 1.0]) {
          const stripe = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.47, 0.24), solid(0xe9ecef, 0.4));
          stripe.position.set(x, 0.75, 0);
          group.add(stripe);
        }
        for (const side of [-1, 1]) {
          const leg = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.75, 0.5), darkMetalMaterial(0x50555b, 0.58));
          leg.position.set(side * 1.25, 0.37, 0);
          group.add(leg);
        }
        break;
      }

      case ObstacleType.Rock: {
        const rockMaterial = solid(0x777167, 0.96);
        const rock = new THREE.Mesh(new THREE.IcosahedronGeometry(1.15, 1), rockMaterial);
        rock.position.y = 0.7;
        rock.rotation.set(Math.random(), Math.random() * Math.PI, Math.random() * 0.5);
        rock.castShadow = true;
        group.add(rock);
        const pebble = new THREE.Mesh(new THREE.IcosahedronGeometry(0.45, 0), solid(0x666158, 0.96));
        pebble.position.set(0.9, 0.3, 0.4);
        group.add(pebble);

        const cracksDamaged = this.buildRockCracks(false);
        cracksDamaged.position.set(0, 0.78, 1.01);
        cracksDamaged.visible = false;
        group.add(cracksDamaged);
        const cracksCritical = this.buildRockCracks(true);
        cracksCritical.position.set(0, 0.78, 1.025);
        cracksCritical.visible = false;
        group.add(cracksCritical);
        group.userData.cracksDamaged = cracksDamaged;
        group.userData.cracksCritical = cracksCritical;
        group.userData.rockMaterial = rockMaterial;
        break;
      }

      case ObstacleType.Wreck: {
        const hull = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.55, 3.6), darkMetalMaterial(0x26282c, 0.7));
        hull.position.y = 0.45;
        hull.rotation.y = 0.4; // skewed across the lane
        hull.castShadow = true;
        group.add(hull);
        const cabin = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.45, 1.5), darkMetalMaterial(0x16191d, 0.64));
        cabin.position.set(0, 0.95, 0.1);
        cabin.rotation.y = 0.4;
        group.add(cabin);
        // scorch glow
        const ember = new THREE.Mesh(
          new THREE.BoxGeometry(0.6, 0.1, 0.6),
          emissiveMaterial(0xff3b17, 4.5)
        );
        ember.position.set(0.2, 0.74, -0.6);
        ember.rotation.y = 0.4;
        group.add(ember);
        break;
      }

      case ObstacleType.RepairKit: {
        const caseMaterial = darkMetalMaterial(0x16242b, 0.42);
        const crate = new THREE.Mesh(new THREE.BoxGeometry(1.35, 1.25, 0.72), caseMaterial);
        crate.position.y = 0.78;
        crate.castShadow = true;
        group.add(crate);

        const panelMaterial = emissiveMaterial(0x34f5e5, 4.4);
        const crossMaterial = emissiveMaterial(0xff2f92, 5.2);
        for (const z of [-0.38, 0.38]) {
          const panel = new THREE.Mesh(new THREE.PlaneGeometry(1.12, 1.02), panelMaterial.clone());
          panel.position.set(0, 0.8, z);
          panel.rotation.y = z < 0 ? Math.PI : 0;
          group.add(panel);

          const vertical = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.7, 0.035), crossMaterial.clone());
          vertical.position.set(0, 0.8, z + Math.sign(z) * 0.012);
          group.add(vertical);
          const horizontal = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.2, 0.035), crossMaterial.clone());
          horizontal.position.copy(vertical.position);
          group.add(horizontal);
        }

        const halo = new THREE.Mesh(
          new THREE.TorusGeometry(0.92, 0.045, 8, 28),
          emissiveMaterial(0x34f5e5, 5.4),
        );
        halo.position.set(0, 0.82, 0.5);
        halo.renderOrder = 5;
        group.add(halo);
        group.userData.repairHalo = halo;

        const beacon = new THREE.Mesh(
          new THREE.CylinderGeometry(0.08, 0.08, 1.2, 8),
          emissiveMaterial(0xff2f92, 4.8),
        );
        beacon.position.y = 2;
        group.add(beacon);
        break;
      }
    }

    return group;
  }

  private paintDamage(visual: ObstacleVisual, healthRatio: number): void {
    if (visual.type !== ObstacleType.Rock) return;
    const tier = healthRatio <= 0.38 ? 2 : healthRatio <= 0.72 ? 1 : 0;
    if (tier === visual.damageTier) return;
    visual.damageTier = tier;
    const damaged = visual.group.userData.cracksDamaged as THREE.Mesh | undefined;
    const critical = visual.group.userData.cracksCritical as THREE.Mesh | undefined;
    if (damaged) damaged.visible = tier >= 1;
    if (critical) critical.visible = tier >= 2;
    const material = visual.group.userData.rockMaterial as THREE.MeshStandardMaterial | undefined;
    if (material) {
      material.color.setHex(tier >= 2 ? 0x5a4d45 : tier === 1 ? 0x696158 : 0x777167);
      material.emissive.setHex(tier >= 2 ? 0x281006 : 0x000000);
      material.emissiveIntensity = tier >= 2 ? 0.42 : 0;
    }
  }

  /** Two transparent fracture decals: dark clefts plus a hot inner edge at critical health. */
  private buildRockCracks(critical: boolean): THREE.Mesh {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext('2d')!;
    const paths = critical
      ? [
          [[126, 20], [116, 66], [142, 99], [119, 132], [135, 178], [104, 238]],
          [[120, 129], [78, 111], [45, 128], [20, 116]],
          [[134, 177], [174, 158], [204, 175], [239, 155]],
          [[116, 66], [82, 53], [59, 25]],
        ]
      : [
          [[126, 32], [116, 75], [140, 108], [118, 148], [132, 211]],
          [[119, 146], [84, 126], [48, 145]],
          [[137, 107], [174, 88], [209, 102]],
        ];

    const stroke = (color: string, width: number, blur: number) => {
      ctx.strokeStyle = color;
      ctx.lineWidth = width;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.shadowColor = color;
      ctx.shadowBlur = blur;
      for (const path of paths) {
        ctx.beginPath();
        path.forEach(([x, y], index) => index === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y));
        ctx.stroke();
      }
      ctx.shadowBlur = 0;
    };
    stroke('rgba(13,7,6,0.96)', critical ? 13 : 10, 0);
    stroke(critical ? 'rgba(255,92,35,0.9)' : 'rgba(98,43,24,0.82)', critical ? 3.4 : 2.2, critical ? 9 : 2);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.magFilter = THREE.LinearFilter;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      toneMapped: false,
    });
    const decal = new THREE.Mesh(new THREE.PlaneGeometry(1.72, 1.72), material);
    decal.renderOrder = 4;
    return decal;
  }
}
